import { X } from 'lucide-react'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface Tag {
  id: string
  name: string
}

interface TagInputProps {
  value: string[]
  onChange: (value: string[]) => void
  availableTags: Tag[]
  onCreateTag?: (name: string) => Promise<Tag>
  placeholder?: string
  className?: string
  disabled?: boolean
}

export function TagInput({
  value,
  onChange,
  availableTags,
  onCreateTag,
  placeholder = 'タグを追加...',
  className,
  disabled = false,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // 選択されたタグのオブジェクト
  const selectedTags = value
    .map((id) => availableTags.find((tag) => tag.id === id))
    .filter(Boolean) as Tag[]

  // 入力値に基づくサジェスト
  const suggestions = inputValue
    ? availableTags.filter(
        (tag) =>
          tag.name.toLowerCase().includes(inputValue.toLowerCase()) && !value.includes(tag.id),
      )
    : availableTags.filter((tag) => !value.includes(tag.id))

  // 新規タグ作成が可能かどうか
  const canCreateNew =
    onCreateTag &&
    inputValue &&
    !availableTags.some((tag) => tag.name.toLowerCase() === inputValue.toLowerCase())

  // タグを追加
  const addTag = async (tag: Tag) => {
    if (!value.includes(tag.id)) {
      onChange([...value, tag.id])
    }
    setInputValue('')
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  // 新しいタグを作成
  const createAndAddTag = async () => {
    if (!canCreateNew || isCreating) return

    setIsCreating(true)
    try {
      const newTag = await onCreateTag!(inputValue)
      await addTag(newTag)
    } catch (error) {
      console.error('Failed to create tag:', error)
    } finally {
      setIsCreating(false)
    }
  }

  // タグを削除
  const removeTag = (tagId: string) => {
    onChange(value.filter((id) => id !== tagId))
  }

  // キーボードハンドリング
  const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (showSuggestions && suggestions.length > 0) {
        // サジェストから選択
        await addTag(suggestions[selectedIndex])
      } else if (canCreateNew) {
        // 新規作成
        await createAndAddTag()
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      setInputValue('')
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // 入力が空でBackspaceを押したら最後のタグを削除
      removeTag(value[value.length - 1])
    }
  }

  // クリックアウトサイドの処理
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // 入力値が変更されたらインデックスをリセット
  useEffect(() => {
    setSelectedIndex(0)
  }, [])

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex flex-wrap gap-2 p-2 border rounded-md bg-white min-h-[42px]',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selectedTags.map((tag) => (
          <Badge key={tag.id} variant="secondary" className="flex items-center gap-1">
            {tag.name}
            {!disabled && (
              <X
                className="h-3 w-3 cursor-pointer hover:opacity-70"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTag(tag.id)
                }}
              />
            )}
          </Badge>
        ))}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          className="flex-1 border-0 p-0 h-auto focus:ring-0 min-w-[100px]"
          disabled={disabled}
        />
      </div>

      {/* サジェストドロップダウン */}
      {showSuggestions && (suggestions.length > 0 || canCreateNew) && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((tag, index) => (
            <div
              key={tag.id}
              className={cn(
                'px-3 py-2 cursor-pointer hover:bg-gray-100',
                selectedIndex === index && 'bg-gray-100',
              )}
              onClick={() => addTag(tag)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {tag.name}
            </div>
          ))}
          {canCreateNew && (
            <div
              className={cn(
                'px-3 py-2 cursor-pointer hover:bg-gray-100 border-t',
                selectedIndex === suggestions.length && 'bg-gray-100',
              )}
              onClick={createAndAddTag}
              onMouseEnter={() => setSelectedIndex(suggestions.length)}
            >
              <span className="text-gray-600">作成: </span>
              <span className="font-medium">{inputValue}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
